import React from 'react';

/*
 * Crisp, scalable seal-style trust badges recreated as SVG (no bitmap crops).
 * Both inherit `currentColor`, so the parent controls the tone (light on the
 * dark footer, muted navy on light pages). viewBox is a fixed 200x200 grid.
 */

const CX = 100;
const CY = 100;

// Scalloped ("postage seal") outer edge for the Arkansas badge.
function scallopPath(r, n) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    pts.push([CX + r * Math.cos(a), CY + r * Math.sin(a)]);
  }
  const rad = ((Math.PI * r) / n) * 1.25;
  let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)} `;
  for (let i = 1; i <= n; i++) {
    const p = pts[i % n];
    d += `A ${rad.toFixed(2)} ${rad.toFixed(2)} 0 0 1 ${p[0].toFixed(2)} ${p[1].toFixed(2)} `;
  }
  return d + 'Z';
}

const SCALLOP = scallopPath(94, 46);

// Pointy-top hexagon for the ISN mark.
const HEX = (() => {
  const r = 38;
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    pts.push(`${(CX + r * Math.cos(a)).toFixed(2)},${(CY + r * Math.sin(a)).toFixed(2)}`);
  }
  return 'M ' + pts.join(' L ') + ' Z';
})();

// Recognizable Arkansas silhouette derived from real state geometry (equirectangular
// projection, centered at origin, ~100 wide). Bootheel notch NE + meandering E river edge.
const AR_STATE = 'M -47.1 -43.6 L 41.4 -43.4 L 43.2 -38.7 L 40.0 -35.7 L 36.8 -31.0 L 50.0 -31.0 L 49.3 -26.4 L 46.3 -25.0 L 45.6 -21.2 L 41.8 -17.1 L 42.2 -11.1 L 40.1 -6.8 L 38.1 -6.1 L 39.3 -3.9 L 36.1 -2.0 L 34.6 2.3 L 32.5 3.3 L 32.8 8.3 L 29.1 9.6 L 29.3 11.3 L 25.0 15.3 L 26.2 18.1 L 22.5 22.0 L 19.3 29.7 L 22.9 32.9 L 21.1 35.0 L 22.2 40.2 L 20.6 43.6 L -29.4 43.2 L -38.2 43.2 L -38.2 29.9 L -41.1 28.8 L -45.2 30.1 L -47.3 27.8 L -46.2 -16.0 L -50.0 -43.6 L -47.1 -43.6 Z';

// Text arcs. Top sweeps left→right over the top; bottom sweeps right→left under
// the bottom so both labels render upright.
const TOP_ARC = `M ${CX - 78} ${CY} A 78 78 0 0 1 ${CX + 78} ${CY}`;
const BOT_ARC = `M ${CX + 78} ${CY} A 78 78 0 0 1 ${CX - 78} ${CY}`;

function Diamond({ x }) {
  return (
    <rect
      x={x - 2.6}
      y={CY - 2.6}
      width={5.2}
      height={5.2}
      transform={`rotate(45 ${x} ${CY})`}
      fill="currentColor"
    />
  );
}

const sealTextStyle = { fontFamily: "'Georgia', 'Times New Roman', serif", fill: 'currentColor' };

export function IsnBadge({ size = 96, className = '', 'data-testid': testId = 'badge-isnetworld' }) {
  const uid = React.useId().replace(/:/g, '');
  const topId = `isn-top-${uid}`;
  const botId = `isn-bot-${uid}`;
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="ISNetworld Member Contractor"
      data-testid={testId}
    >
      <title>ISNetworld Member Contractor</title>
      <defs>
        <path id={topId} d={TOP_ARC} fill="none" />
        <path id={botId} d={BOT_ARC} fill="none" />
      </defs>
      <circle cx={CX} cy={CY} r="96" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx={CX} cy={CY} r="66" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
      <text style={sealTextStyle} fontSize="16.5" fontWeight="600" letterSpacing="2.5" textAnchor="middle">
        <textPath href={`#${topId}`} startOffset="50%">ISNETWORLD</textPath>
      </text>
      <text style={sealTextStyle} fontSize="13" fontWeight="600" letterSpacing="1.6" textAnchor="middle">
        <textPath href={`#${botId}`} startOffset="50%">MEMBER CONTRACTOR</textPath>
      </text>
      <Diamond x={CX - 88} />
      <Diamond x={CX + 88} />
      <path d={HEX} fill="currentColor" fillOpacity="0.07" stroke="currentColor" strokeWidth="2" />
      <text
        style={sealTextStyle}
        x={CX}
        y={CY + 8}
        fontSize="26"
        fontWeight="700"
        letterSpacing="1"
        textAnchor="middle"
      >
        ISN
      </text>
    </svg>
  );
}

export function ArkansasBadge({ size = 96, className = '', 'data-testid': testId = 'badge-arkansas' }) {
  const uid = React.useId().replace(/:/g, '');
  const topId = `ar-top-${uid}`;
  const botId = `ar-bot-${uid}`;
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="Arkansas Based — Since 1999"
      data-testid={testId}
    >
      <title>Proudly Arkansas Based — Since 1999</title>
      <defs>
        <path id={topId} d={TOP_ARC} fill="none" />
        <path id={botId} d={BOT_ARC} fill="none" />
      </defs>
      <path d={SCALLOP} fill="none" stroke="currentColor" strokeWidth="1.75" />
      <circle cx={CX} cy={CY} r="70" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
      <text style={sealTextStyle} fontSize="15.5" fontWeight="600" letterSpacing="2.2" textAnchor="middle">
        <textPath href={`#${topId}`} startOffset="50%">ARKANSAS BASED</textPath>
      </text>
      <text style={sealTextStyle} fontSize="14" fontWeight="600" letterSpacing="2.4" textAnchor="middle">
        <textPath href={`#${botId}`} startOffset="50%">SINCE 1999</textPath>
      </text>
      <Diamond x={CX - 82} />
      <Diamond x={CX + 82} />
      <g transform={`translate(${CX} ${CY - 1}) scale(0.86)`}>
        <path d={AR_STATE} fill="currentColor" fillOpacity="0.9" />
        {/* Conway, AR (headquarters) at its true location */}
        <circle cx="-5.5" cy="-8.4" r="3.6" fill="#ffffff" fillOpacity="0.9" />
      </g>
    </svg>
  );
}

export function TrustBadgeRow({ className = '', badgeSize = 76 }) {
  return (
    <div className={`flex items-center justify-center gap-8 ${className}`} data-testid="trust-badge-row">
      <IsnBadge size={badgeSize} />
      <ArkansasBadge size={badgeSize} />
    </div>
  );
}

export default TrustBadgeRow;
